import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Grid,
  Typography,
  Divider as MuiDivider,
  CircularProgress,
  IconButton
} from '@material-ui/core';
import { Close as CloseIcon } from '@material-ui/icons';
import styled from 'styled-components';
import { spacing } from '@material-ui/system';
import { getAssessmentDetails } from '../../common/apiGetUtilities';
import { useTranslation } from 'react-i18next';
import { useUserState } from '../../context/UserContext';

const Divider = styled(MuiDivider)(spacing);

const StyledDialogTitle = styled(DialogTitle)`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const InfoLabel = styled(Typography)`
  font-weight: 600;
  color: #666;
  font-size: 0.875rem;
  margin-bottom: 4px;
`;

const InfoValue = styled(Typography)`
  font-size: 1rem;
  margin-bottom: 16px;
`;

const ClassificationBadge = styled.span`
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 0.875rem;
  font-weight: 600;
  background-color: ${props => props.classification === 'at_risk' ? '#ffebee' : '#e8f5e9'};
  color: ${props => props.classification === 'at_risk' ? '#c62828' : '#2e7d32'};
`;

const AssessmentDetailsPopup = ({ open, assessmentId, onClose }) => {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { t, i18n } = useTranslation();
  const user = useUserState();

  useEffect(() => {
    if (open && assessmentId && user) {
      setLoading(true);
      setError(null);
      getAssessmentDetails(user, assessmentId)
        .then(data => {
          setDetails(data);
          setLoading(false);
        })
        .catch(err => {
          console.error('Error fetching assessment details:', err);
          setError('Failed to load assessment details');
          setLoading(false);
        });
    }
  }, [open, assessmentId, user]);

  const handleClose = () => {
    setDetails(null);
    setError(null);
    onClose();
  };

  if (!open) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <StyledDialogTitle>
        <Typography variant="h6">Assessment Details</Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </StyledDialogTitle>
      <DialogContent>
        {loading && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
            <CircularProgress />
          </div>
        )}

        {error && (
          <Typography color="error" align="center" style={{ padding: '20px' }}>
            {error}
          </Typography>
        )}

        {!loading && !error && details && (
          <Grid container spacing={2}>
            {/* Basic Information */}
            <Grid item xs={12}>
              <InfoLabel>📅 Assessment Date</InfoLabel>
              <InfoValue>
                {details.assessment.date ?
                  new Date(details.assessment.date).toLocaleDateString(
                    i18n.language === 'es' ? 'es-ES' : 'en-US',
                    { year: 'numeric', month: '2-digit', day: '2-digit' }
                  ) : 'N/A'}
              </InfoValue>
            </Grid>

            <Grid item xs={12}>
              <InfoLabel>🆔 Assessment ID</InfoLabel>
              <InfoValue style={{ fontSize: '0.875rem', wordBreak: 'break-all' }}>
                {details.assessment._id || 'N/A'}
              </InfoValue>
            </Grid>

            {details.parent && (
              <>
                <Grid item xs={12}>
                  <InfoLabel>👤 Parent Email</InfoLabel>
                  <InfoValue>{details.parent.email || 'N/A'}</InfoValue>
                </Grid>

                {details.parent.name && (
                  <Grid item xs={12}>
                    <InfoLabel>👤 Parent Name</InfoLabel>
                    <InfoValue>{details.parent.name}</InfoValue>
                  </Grid>
                )}
              </>
            )}

            {details.subject && (
              <>
                <Grid item xs={12}>
                  <InfoLabel>👶 Child DOB</InfoLabel>
                  <InfoValue>
                    {details.subject.dob ?
                      new Date(details.subject.dob).toLocaleDateString(
                        i18n.language === 'es' ? 'es-ES' : 'en-US',
                        { year: 'numeric', month: '2-digit', day: '2-digit' }
                      ) : 'N/A'}
                  </InfoValue>
                </Grid>
              </>
            )}

            {details.studyParticipant && details.studyParticipant.studyid && (
              <Grid item xs={12}>
                <InfoLabel>🔬 Study ID</InfoLabel>
                <InfoValue style={{ fontSize: '0.875rem', wordBreak: 'break-all' }}>
                  {details.studyParticipant.studyid}
                </InfoValue>
              </Grid>
            )}

            {details.assessment.notes?.age && (
              <Grid item xs={12}>
                <InfoLabel>📊 Age at Assessment</InfoLabel>
                <InfoValue>{details.assessment.notes.age} weeks</InfoValue>
              </Grid>
            )}

            {/* Metrics Section */}
            {details.assessment.notes && (details.assessment.notes.z !== undefined || details.assessment.notes.percentile !== undefined) && (
              <>
                <Grid item xs={12}>
                  <Divider my={2} />
                </Grid>

                {details.assessment.notes.z !== undefined && (
                  <Grid item xs={6}>
                    <InfoLabel>📈 Z-Score</InfoLabel>
                    <InfoValue>{details.assessment.notes.z.toFixed(3)}</InfoValue>
                  </Grid>
                )}

                {details.assessment.notes.percentile !== undefined && (
                  <Grid item xs={6}>
                    <InfoLabel>📊 Percentile</InfoLabel>
                    <InfoValue>{details.assessment.notes.percentile}</InfoValue>
                  </Grid>
                )}

                {details.assessment.notes.classification && (
                  <Grid item xs={6}>
                    <InfoLabel>⚖️ Classification</InfoLabel>
                    <InfoValue>
                      <ClassificationBadge classification={details.assessment.notes.classification}>
                        {details.assessment.notes.classification}
                      </ClassificationBadge>
                    </InfoValue>
                  </Grid>
                )}

                {details.assessment.notes.quality_index !== undefined && (
                  <Grid item xs={6}>
                    <InfoLabel>🎯 Quality Index</InfoLabel>
                    <InfoValue>{details.assessment.notes.quality_index.toFixed(3)}</InfoValue>
                  </Grid>
                )}
              </>
            )}

            {/* Feature Contributions */}
            {details.assessment.notes?.feature_contributions?.length > 0 && (
              <>
                <Grid item xs={12}>
                  <Divider my={2} />
                </Grid>

                <Grid item xs={12}>
                  <InfoLabel>🔬 Top Contributing Features</InfoLabel>
                  {details.assessment.notes.feature_contributions.slice(0, 3).map((fc, idx) => (
                    <Typography key={idx} variant="body2" style={{ marginBottom: '4px' }}>
                      {idx + 1}. {fc.feature} (z: {fc.z_impact?.toFixed(1) || fc.z?.toFixed(1) || 'N/A'})
                    </Typography>
                  ))}
                </Grid>
              </>
            )}
          </Grid>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AssessmentDetailsPopup;
